import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/ErrorBoundary';
import NavBar from './components/NavBar';
import AuthGate from './components/AuthGate';

import MapPage from './pages/MapPage';
import ChargerDetailPage from './pages/ChargerDetailPage';
import AuthPage from './pages/AuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OnboardingPage from './pages/OnboardingPage';
import LeaderboardPage from './pages/LeaderboardPage';
import ReferralPage from './pages/ReferralPage';
import PremiumPage from './pages/PremiumPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import OperatorAuthPage from './pages/OperatorAuthPage';
import OperatorDashboardPage from './pages/OperatorDashboardPage';
import NotFoundPage from './pages/NotFoundPage';

const TOAST_OPTS = {
  style: {
    background: 'var(--slate)', color: 'var(--text-1)',
    border: '1px solid var(--border-2)', borderRadius: 10, fontSize: 13, maxWidth: 360,
  },
  success: { iconTheme: { primary: '#00D4AA', secondary: '#F0F4FF' } },
  error:   { iconTheme: { primary: '#EF4444', secondary: '#F0F4FF' } },
};

const NO_NAV = ['/login', '/signup', '/operator', '/admin', '/welcome', '/forgot-password', '/reset-password'];

function PremiumSuccess() {
  return (
    <div style={{ minHeight:'100vh', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif', textAlign:'center', padding:24 }}>
      <div style={{ maxWidth:380 }}>
        <div style={{ width:72, height:72, borderRadius:20, background:'linear-gradient(135deg,#00D4AA,#0099FF)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, margin:'0 auto 20px', boxShadow:'0 0 40px rgba(0,212,170,0.3)' }}>⚡</div>
        <h1 style={{ fontSize:26, fontWeight:800, color:'var(--teal)', marginBottom:10 }}>You're Premium!</h1>
        <p style={{ color:'var(--text-2)', fontSize:15, marginBottom:28, lineHeight:1.65 }}>
          Ad-free experience, unlimited saved chargers, and instant alerts are now active.
        </p>
        <a href="/" style={{ display:'inline-block', padding:'12px 28px', borderRadius:10, background:'linear-gradient(135deg,#00D4AA,#0099FF)', color:'#F0F4FF', fontWeight:700, fontSize:14, textDecoration:'none' }}>
          Open Map
        </a>
      </div>
    </div>
  );
}

function Layout() {
  const location = useLocation();
  const hideNav = NO_NAV.some(p => location.pathname.startsWith(p));
  return (
    <ErrorBoundary>
      {!hideNav && <NavBar />}
      <Routes>
        {/* Public */}
        <Route path="/" element={<MapPage />} />
        <Route path="/charger/:slug" element={<ChargerDetailPage />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/signup" element={<AuthPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/welcome" element={<OnboardingPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/premium" element={<PremiumPage />} />
        <Route path="/premium/success" element={<PremiumSuccess />} />

        {/* Auth-gated */}
        <Route path="/invite" element={
          <AuthGate title="Invite friends, earn points" description="Sign in to get your personal referral code. You and every friend you invite both earn 20 points." icon="🎁">
            <ReferralPage />
          </AuthGate>
        } />
        <Route path="/profile" element={
          <AuthGate title="Your profile" description="Sign in to see your points, badges, saved chargers, and account settings." icon="👤">
            <ProfilePage />
          </AuthGate>
        } />

        {/* Operator */}
        <Route path="/operator" element={<OperatorAuthPage />} />
        <Route path="/operator/dashboard" element={<OperatorDashboardPage />} />

        {/* Admin */}
        <Route path="/admin" element={<AdminPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-center" toastOptions={TOAST_OPTS} />
      <Layout />
    </BrowserRouter>
  );
}
